import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TablePagination,
  Button,
  IconButton,
  Menu,
  MenuItem,
  Snackbar,
  Alert,
  CircularProgress,
} from '@mui/material';
import { AppDispatch } from '../../app/store';
import { selectCustomer } from '../../features/customer/customerSlice';
import { deleteCustomer, getCustomers } from '../../features/customer/customerActions';
import CustomerForm from './CustomerForm';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import ConfirmationModal from '../common/confirmationModal';
import { hasPermission } from '../../utils/checkPermission';
import { PERMISSIONS } from '../../core/permissions';
import CustomerPaymentForm from '../ca-transaction/CustomerPaymentForm';
import { Link } from "react-router-dom";

const CustomerList = () => {
  const dispatch = useDispatch<AppDispatch>();
  const customerState = useSelector(selectCustomer);
  const { items: customers = [], currentPage, totalCount } = customerState.customers;
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [openModal, setOpenModal] = useState(false);
  const [openPaymentModal, setOpenPaymentModal] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedCustomerId, setSelectedCustomerId] = useState(null);
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [confirmationModalOpen, setConfirmationModalOpen] = useState(false);
  const {isError, error, loading} = useSelector(selectCustomer);
  const [selectedCustomer, setSelectedCustomer] = useState(null);


  const openConfirmationModal = () => {
    setConfirmationModalOpen(true);
  };

  const closeConfirmationModal = () => {
    setConfirmationModalOpen(false);
  };

  const handleConfirmAction = () => {
    handleDeleteCustomer();
    closeConfirmationModal();
  };


  const showSnackbar = (message: string, severity: "success" | "error") => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };

  useEffect(() => {
    dispatch(getCustomers(page + 1, rowsPerPage));
  }, [dispatch, page, rowsPerPage]);

  const handleChangePage = (_: any, newPage: React.SetStateAction<number>) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: { target: { value: string; }; }) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleOpenModal = () => {
    setOpenModal(true);
  };

  const handleOpenPaymentModal = () => {
    setOpenPaymentModal(true);
  };

  const handleClosePaymentModal = () => {
    setOpenPaymentModal(false);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    setSelectedCustomer(null);
    setSelectedCustomerId(null);
  };

  const handleUpdateCustomer = () => {
    handleOpenModal();
    handleMenuClose();
  };

  const handleDeleteCustomer = () =>{
    handleMenuClose();
    if (selectedCustomerId !== null) {
      dispatch(deleteCustomer(selectedCustomerId))
        .then(() => {
          if(!isError && !loading){
            showSnackbar('Cutomer deleted successfully', 'success');
          }else{
            showSnackbar(error, 'error');
          }
        })
      }
  }

  const handleMenuOpen = (event: any, customerId: any, customer: any) => {
    setAnchorEl(event.currentTarget);
    setSelectedCustomerId(customerId);
    setSelectedCustomer(customer);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedCustomerId(null);
  };

  const handleCloseSnackbar = () => {
    setSnackbarOpen(false);
  };

  if (!customers) {
    return <CircularProgress />;
  }

  return (
    <div>
      {hasPermission(PERMISSIONS.CreateCustomer) && <Button variant="contained" color="primary" onClick={handleOpenModal}>
       Add Customer
      </Button>}
      {hasPermission(PERMISSIONS.CreateCustomerPayment) && <Button variant="contained" color="primary" onClick={handleOpenPaymentModal} style={{marginLeft:'10px'}}>
       Add Customer Payment
      </Button>}
      <TablePagination
         rowsPerPageOptions={[5, 10, 25]}
         component="div"
         count={totalCount || 0}
         rowsPerPage={rowsPerPage}
         page={(currentPage && currentPage > 0) ? currentPage - 1 : 0}
         onPageChange={handleChangePage}
         onRowsPerPageChange={handleChangeRowsPerPage}
      />
      {hasPermission(PERMISSIONS.GetCustomers) && <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Tin number</TableCell>
              <TableCell>Phone number</TableCell>
              <TableCell>Address</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {customers.map((customer: any) => (
              <TableRow
              key={customer.id}
              component={Link}
              to={`/customers/${customer.id}`}
              style={{ textDecoration: "none" }}
            >
                <TableCell>{customer.firstName + " " + customer.lastName }</TableCell>
                <TableCell>{customer.tinNumber}</TableCell>
                <TableCell>{customer.phone}</TableCell>
                <TableCell>{customer.address}</TableCell>
                <TableCell>
                <IconButton
                    aria-label="Actions"
                    onClick={(event) =>{ 
                      event.stopPropagation();
                      event.preventDefault(); // Prevent default behavior
                      handleMenuOpen(event, customer.id, customer)
                    }}
                    style={{ margin: 0, padding: 0 }}
                  >
                   <MoreVertIcon/>
                  </IconButton>
                  <Menu
                    id="actions-menu"
                    MenuListProps={{
                      'aria-labelledby': 'long-button',
                    }}
                    anchorEl={anchorEl}
                    open={Boolean(anchorEl)}
                    onClose={handleMenuClose}
                    PaperProps={{
                      style: {
                        width: '20ch',
                        boxShadow: '0px 1px 2px rgba(0, 0, 0, 0.1)'
                      },
                    }}
                  >
                    {hasPermission(PERMISSIONS.UpdateCustomer) && 
                    <MenuItem onClick={ (event) =>{
                        event.stopPropagation();
                        event.preventDefault(); // Prevent default behavior
                      handleUpdateCustomer()}}>Update</MenuItem>}
                    {hasPermission(PERMISSIONS.DeleteCustomer) && 
                    <MenuItem onClick={(event)=>{
                      event.stopPropagation();
                      event.preventDefault(); // Prevent default behavior
                      openConfirmationModal()}}>Delete</MenuItem>}
                  </Menu>
                  </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>}
      <CustomerForm open={openModal} handleClose={handleCloseModal} selectedCustomer={selectedCustomer}/>
      <CustomerPaymentForm
        open={openPaymentModal}
        handleClose={() => handleClosePaymentModal()}
      />
      <ConfirmationModal
        open={confirmationModalOpen}
        onClose={closeConfirmationModal}
        onConfirm={handleConfirmAction}
        title="Delete Customer"
        content="Are you sure you want to delete this customer?"
      />
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbarSeverity as "success" | "error"}
          sx={{ width: "100%" }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </div>
  );
};

export default CustomerList;
